/// <amd-module name="Wit/SprintsHub/SprintsDirectory/Components/SprintsDirectoryView" />
import * as React from "react";
import { ObservableValue } from "VSS/Core/Observable";
import { onClickFPS } from "VSS/Platform/FPS";
import { Components, IVssComponentContext, IVssLayoutManager, VssComponent } from "VSS/Platform/Layout";
import { routeUrl } from "VSS/Platform/Location";
import { Button } from "VSSUI/Button";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { SimpleTableCell } from "VSSUI/Table";
import { ITreeColumn } from "VSSUI/TreeEx";
import { ITreeItemEx } from "VSSUI/Utilities/TreeItemProvider";
import { getDateInUTC } from "Wit/Common/DateUtils";
import { FavoriteTypes } from "Wit/Common/Generated/Constants";
import { SprintEditorDialog } from "Wit/Common/SprintEditorDialog/SprintEditorDialog";
import { SprintDirectoryItem } from "Wit/DirectoryService/Client/Contracts/Directory";
import { IDirectoryTreeItem } from "Wit/DirectoryService/Models";
import { DirectoryView } from "Wit/DirectoryView/Components/DirectoryView";
import { SprintsTabs } from "Wit/SprintsHub/Common/Constants";
import { ISprintsDirectoryService } from "Wit/SprintsHub/Common/ISprintsDirectoryService";
import * as Resources from "Wit/SprintsHub/SprintsDirectory/Resources";

const dateFormatter = new Intl.DateTimeFormat(undefined, { year: "numeric", month: "numeric", day: "numeric" });

export class SprintsDirectoryView extends VssComponent {
    private directoryService: ISprintsDirectoryService;

    public constructor(props: {}, context: IVssComponentContext) {
        super(props, context);
        this.directoryService = context.pageContext.getService<ISprintsDirectoryService>("ISprintsDirectoryService");
    }

    public render(): JSX.Element {
        return (
            <DirectoryView<SprintDirectoryItem>
                additionalColumns={this.additionalColumns}
                additionalHeaderItem={this.additionalHeaderItem}
                artifactType={FavoriteTypes.WORK_ITERATION}
                directoryItemName={Resources.SprintsArtifactName}
                pageTitle={Resources.SprintsHubTitle}
                rowIconName="Sprint"
                directoryService={this.directoryService}
                treeAriaLabel={Resources.SprintsDirectoryTableAriaLabel}
                zeroDataContributionId={"ms.vss-work-web/agile-assets/Content/no-results-found.svg"}
            />
        );
    }

    private onSize = (event: MouseEvent, index: number, width: number, column: ITreeColumn<IDirectoryTreeItem<SprintDirectoryItem>>) => {
        (column.width as ObservableValue<number>).value = width;
    };

    private renderSprintNameCell = (
        rowIndex: number,
        columnIndex: number,
        treeColumn: ITreeColumn<IDirectoryTreeItem<SprintDirectoryItem>>,
        treeItem: ITreeItemEx<IDirectoryTreeItem<SprintDirectoryItem>>
    ): JSX.Element => {
        const key = `sprint-name-${rowIndex}-${columnIndex}`;
        const item = treeItem.underlyingItem.data;
        const directoryItem = item.directoryItem;
        return (
            <SimpleTableCell ariaRowIndex={rowIndex} contentClassName="bolt-table-cell-content-with-button" columnIndex={columnIndex} key={key}>
                {!item.isFolder && directoryItem.iterationName}
            </SimpleTableCell>
        );
    };

    private renderSprintDatesCell = (
        rowIndex: number,
        columnIndex: number,
        treeColumn: ITreeColumn<IDirectoryTreeItem<SprintDirectoryItem>>,
        treeItem: ITreeItemEx<IDirectoryTreeItem<SprintDirectoryItem>>
    ): JSX.Element => {
        const key = `sprint-dates-${rowIndex}-${columnIndex}`;
        const item = treeItem.underlyingItem.data;
        const directoryItem = item.directoryItem;
        let datesString: string;
        if (directoryItem.iterationStartDate != null && directoryItem.iterationEndDate != null) {
            datesString =
                dateFormatter.format(getDateInUTC(directoryItem.iterationStartDate)) +
                " - " +
                dateFormatter.format(getDateInUTC(directoryItem.iterationEndDate));
        } else {
            datesString = Resources.SetDates;
        }

        return (
            <SimpleTableCell ariaRowIndex={rowIndex} contentClassName="bolt-table-cell-content-with-button" columnIndex={columnIndex} key={key}>
                {!item.isFolder && (
                    <Button
                        className="sprint-dates-button"
                        subtle={true}
                        text={`${datesString}`}
                        onClick={(event) => this.openEditSprintDialog(event, item.directoryItem)}
                    />
                )}
            </SimpleTableCell>
        );
    }

    private openDialog(callout: (onDismiss: () => void) => JSX.Element): void {
        const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout(callout);
    }

    private openEditSprintDialog = (event: React.MouseEvent<HTMLElement, MouseEvent> | React.KeyboardEvent<HTMLElement>, item: SprintDirectoryItem) => {
        event.preventDefault();
        this.openDialog(onDismiss => {
            return (
                <SprintEditorDialog
                    endDate={item.iterationEndDate}
                    iterationGuid={item.iterationId}
                    iterationName={item.iterationName}
                    startDate={item.iterationStartDate}
                    showTeamDropdown={false}
                    teamId={item.id}
                    teamName={item.name}
                    onDismiss={onDismiss}
                />);
        });
    }

    private navigateToSprint = (routeValues: { [key: string]: string }) => {
        if (!routeValues["pivot"]) {
            routeValues["pivot"] = SprintsTabs.Taskboard;
        }
        const url = routeUrl(this.context.pageContext, "ms.vss-work-web.new-sprints-content-route", routeValues);
        onClickFPS(this.context.pageContext, url, true);
    }

    private openNewSprintDialog = () => {
        this.openDialog(onDismiss => {
            return (<SprintEditorDialog onDismiss={onDismiss} showTeamDropdown={true} navigateToSprintOnComplete={this.navigateToSprint} />);
        });
    }

    private additionalColumns = [
        {
            id: "current-sprint",
            name: Resources.CurrentSprintLabel,
            width: new ObservableValue(-60),
            renderCell: this.renderSprintNameCell,
            onSize: this.onSize
        },
        {
            id: "current-sprint-dates",
            name: Resources.CurrentSprintDatesLabel,
            width: new ObservableValue(-40),
            renderCell: this.renderSprintDatesCell,
            onSize: this.onSize
        }
    ];

    private additionalHeaderItem: IHeaderCommandBarItem = {
        id: "new-sprint",
        isPrimary: true,
        text: Resources.NewSprint,
        onActivate: this.openNewSprintDialog
    }
}

Components.add("sprints-directory-view-component", SprintsDirectoryView);
